import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useCallback } from 'react'
import { globalQueryKeys } from '@opallabs/api'
import { moments, OpalApiResponseDocument } from '@opallabs/api/v3'
import {
  effectivePrivacyMap,
  type ReadableAccessMetadata,
} from '@opallabs/concepts'

type UseReadMomentAccessParams = UseQueryOptions<
  OpalApiResponseDocument<'ReadMomentV3', 200>
> & {
  momentUuid: string
}

type Selector = (
  responseDocument: OpalApiResponseDocument<'ReadMomentV3', 200>
) => ReadableAccessMetadata | undefined

export const useReadMomentAccess = (params: UseReadMomentAccessParams) =>
  useQuery({
    queryKey: globalQueryKeys.momentAccess(params.momentUuid),
    queryFn: () =>
      moments.readSingle({
        path: {
          moment_id: params.momentUuid,
        },
        query: {
          fields: {
            moment: 'meta',
          },
          expose: {
            moment: {
              meta: 'effective_privacy',
            },
            meta: {
              access: 'true',
            },
          },
        },
      }),
    enabled: params.enabled,
    refetchOnMount: params.refetchOnMount,
    useErrorBoundary: params.useErrorBoundary,
    retry: params.retry,
    select: useCallback<Selector>((responseDocument) => {
      if (!responseDocument.meta?.access) return undefined
      const effectivePrivacyValue =
        responseDocument.data.meta?.effective_privacy

      return {
        ...responseDocument.meta.access,
        effectivePrivacy:
          effectivePrivacyValue != null
            ? effectivePrivacyMap[effectivePrivacyValue]
            : null,
      }
    }, []),
  })
